import styles from "./variableItem.module.scss";
import {
  Dropdown,
  DropdownItem
} from "../../../shared/components/dropdown/dropdown";
import addBlockIcon from "../../../shared/images/addBlock.svg";
import {
  FieldValues,
  UseControllerProps
} from "react-hook-form";
import {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import { useGetAllVariablesQuery } from "../../../api/variableService";
import deleteIcon from "../../../shared/images/delete.svg";
import editIcon from "../../../shared/images/edit.svg";
import {
  Variable,
  VariableValue
} from "../../../types/variableType";
import { VariableValueModal } from "../variableValueModal/variableValueModal";
import { DeleteVariableValueModal } from "../deleteVariableValueModal/deleteVariableValueModal";

type VariableItemProps<T extends FieldValues> = {
  variableId: number;
  updateFormValue: (variable: Variable, newVariableId: number) => void;
} & UseControllerProps<T>

export const VariableItem = <T extends FieldValues>({ control, name, updateFormValue, variableId }: VariableItemProps<T>) => {

  const [variable, setVariable] = useState<Variable>();

  const [isOpenEnvironmentDropdown, setIsOpenEnvironmentDropdown] = useState<boolean>(false);

  const [showValueModal, setShowValueModal] = useState<boolean>(false);
  const [editVariableValue, setEditVariableValue] = useState<VariableValue>();
  const [showDeleteVariableModal, setShowDeleteVariableModal] = useState<boolean>(false);

  const { data: variables, refetch: refetchVariables} = useGetAllVariablesQuery("");

  useEffect(() => {
    if (!!variables) {
      setVariable(variables.find(item => item.id === variableId));
    }
  }, [variables, variableId, setVariable]);


  const onClickAddValueToVariable = useCallback((): void => {
    setShowValueModal(true);
  }, [setShowValueModal]);

  const clickEditValue = useCallback((e: MouseEvent<HTMLDivElement>, value: VariableValue) => {
    e.stopPropagation();
    setShowValueModal(true);
    setEditVariableValue(value);
    setIsOpenEnvironmentDropdown(false);
  }, [setShowValueModal, setEditVariableValue, setIsOpenEnvironmentDropdown]);

  const clickDeleteValue = useCallback((e: MouseEvent<HTMLDivElement>, value: VariableValue) => {
    e.stopPropagation();
    setShowDeleteVariableModal(true);
    setEditVariableValue(value);
    setIsOpenEnvironmentDropdown(false);
  }, [setShowDeleteVariableModal, setEditVariableValue, setIsOpenEnvironmentDropdown]);

  const onCloseVariableValueModal = useCallback((isSave: boolean, newVariableId?: number) => {
    if (isSave) {
      if (newVariableId && variable) {
        updateFormValue(variable, newVariableId);
      }
      refetchVariables();
    }
    setEditVariableValue(undefined);
    setShowValueModal(false);
  }, [setEditVariableValue, setShowValueModal, updateFormValue, refetchVariables, variable]);

  const onCloseDeleteVariableValueModal = useCallback((isSave: boolean, newVariableId?: number) => {
    if (isSave) {
      if (!!newVariableId && variable) {
        updateFormValue(variable, newVariableId);
      }
      refetchVariables();
    }
    setEditVariableValue(undefined);
    setShowDeleteVariableModal(false);
  }, [setEditVariableValue, setShowDeleteVariableModal, updateFormValue, refetchVariables, variable]);

  const dropdownItem = useMemo((): Array<DropdownItem> => {
    return variable?.values.map(value => ({
      id: value.id,
      name: (
        <div className={styles.option}>
          <div className={styles.info}>
            <span title={value.value}>{value.value}</span>
          </div>
          <div className={styles.actionsContainer}>
            { variable.values.length !== 1 && (
              <div className={styles.item} onClick={(e) => clickDeleteValue(e, value)}>
                <img src={deleteIcon} alt=""/>
              </div>
            )}
            <div className={styles.item} onClick={(e) => clickEditValue(e, value)}>
              <img src={editIcon} alt=""/>
            </div>
          </div>
        </div>
      ),
      title: value.value
    })) || []
  }, [variable, clickDeleteValue, clickEditValue]);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.name}>{variable?.name}</div>
        <div className={styles.inputContainer}>
          <div className={styles.dropdown}>
            <Dropdown items={dropdownItem}
                      control={control}
                      isOpenProp={isOpenEnvironmentDropdown}
                      setIsOpenProp={setIsOpenEnvironmentDropdown}
                      fontPopupClass={styles.fontDropdownPopup}
                      selectedItemClass={styles.selectedItemDropdown}
                      name={name}/>
          </div>
          <img className={styles.addBlock}
               onClick={() => onClickAddValueToVariable()}
               src={addBlockIcon} alt=""/>
        </div>
      </div>

      {showValueModal && !!variable && (
        <VariableValueModal editVariable={variable}
                            editVariableValue={editVariableValue}
                            onClose={onCloseVariableValueModal}/>
      )}

      {showDeleteVariableModal && !!variable && !!editVariableValue && (
        <DeleteVariableValueModal deleteVariableValue={editVariableValue}
                                  editVariable={variable}
                                  onClose={onCloseDeleteVariableValueModal}/>
      )}
    </>

  )
}